import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import type { ProgramCalendarEventType } from "@/modules/calendar/calendarTypes"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockEntityType } from "@/modules/sessions/entities/sessionBlockEntity"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useMutation } from "@tanstack/react-query"

export const useSessionBlockSort = () => {
  const { programInfiniteSessionApi } = useServicesContext()

  const { mutateAsync: moveBlockMutation, isPending } = useMutation({
    mutationFn: async ({
      sessionId,
      blockIndex,
      newBlockIndex,
    }: {
      sessionId: string
      blockIndex: number
      newBlockIndex: number
    }) => {
      await programInfiniteSessionApi.moveBlock(sessionId, blockIndex, newBlockIndex)
    },
    // When mutate is called:
    onMutate: async ({ sessionId, blockIndex, newBlockIndex }, context) => {
      // Cancel any outgoing refetch
      // (so they don't overwrite our optimistic update)
      await context.client.cancelQueries({ queryKey: ["programSessions"] })

      // Snapshot the previous value
      const previousSessionsData = context.client.getQueriesData<
        {
          queryKey: readonly unknown[]
          data: ProgramCalendarEventType[]
        }[]
      >({ queryKey: ["programSessions"] })

      // Optimistically update to the new value
      if (previousSessionsData) {
        context.client.setQueriesData(
          { queryKey: ["programSessions"] },
          (oldData: {
            events: ProgramCalendarEventType[]
            sessions: ProgramInfiniteSessionEntity[] | ProgramOnOffSessionEntity[]
          }) => {
            return {
              sessions: oldData.sessions?.map((item) => {
                if (item.id === sessionId) return sortSessionBlocks(item, blockIndex, newBlockIndex)
                return item
              }),
              events: oldData.events?.map((item) => {
                if (item.id === sessionId) return sortSessionBlocks(item, blockIndex, newBlockIndex)
                return item
              }),
            }
          },
        )
      }

      return { previousSessionsData }
    },
    // Always refetch after error or success:
    onSettled: (_data, error, _variables, _onMutateResult, context) => {
      if (error) {
        toast.error(word("global.error.default"), {
          classNames: errorToastClassName(),
        })
      }
      context.client.invalidateQueries({ queryKey: ["programSessions"] })
    },
  })

  return {
    moveBlockMutation,
    isPending,
  }
}

const sortSessionBlocks = (
  item: ProgramCalendarEventType | ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
  blockIndex: number,
  newBlockIndex: number,
) => {
  // Create a copy sorted by content.orderIndex
  const sortedBlocks = [...item.blocks].sort((a, b) => (a?.content?.orderIndex ?? 0) - (b?.content?.orderIndex ?? 0))
  // Guard against out-of-range indices
  if (newBlockIndex >= 0 && newBlockIndex < sortedBlocks.length) {
    // Swap the blocks at blockIndex and newIndex
    const temp = sortedBlocks[blockIndex]
    sortedBlocks[blockIndex] = sortedBlocks[newBlockIndex]!
    sortedBlocks[newBlockIndex] = temp!
    // Normalize orderIndex to match the new order
    const normalizedBlocks = sortedBlocks.map((block: SessionBlockEntityType, idx: number) => ({
      ...block,
      content: {
        ...block.content,
        orderIndex: idx,
      },
    }))
    return {
      ...item,
      blocks: normalizedBlocks,
    }
  }
}
